// src/pages/BookAppointment.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css'; // Import the CSS for react-datepicker
import { format, toDate } from 'date-fns-tz';
import { apiUrl } from '../utils/api';

const timeZone = 'Asia/Kolkata'; // Define the desired time zone

const BookAppointment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    appointmentDate: new Date(), // Default to current date
    reason: '',
  });
  const [unavailableDates, setUnavailableDates] = useState([]); // Store unavailable dates
  const [availableSlots, setAvailableSlots] = useState([]); // Store available slots

  // Fetch unavailable slots from the backend
  const fetchUnavailableDates = async () => {
    try {
      const response = await fetch(apiUrl('/api/bookings/unavailable-dates'));
      if (!response.ok) throw new Error('Failed to fetch unavailable dates');
      const data = await response.json();
      setUnavailableDates(data.map((date) => new Date(date))); // Convert to Date objects
    } catch (error) {
      console.error('Error fetching unavailable dates:', error);
    }
  };

  // Fetch available slots for the selected date
  const fetchAvailableSlots = async (date) => {
    try {
      const response = await fetch(apiUrl(`/api/bookings/available-slots?date=${date.toISOString()}`));
      if (!response.ok) throw new Error('Failed to fetch available slots');
      const data = await response.json();

      const { timeZone, availableSlots } = data;

      // Convert slots from UTC to the specified time zone
      const slotsInTimeZone = availableSlots.map((slot) => {
        const utcDate = new Date(slot); // Parse the UTC date
        const zonedTime = toDate(utcDate, { timeZone }); // Convert to the specified time zone
        return zonedTime;
      });

      console.log('Converted Slots:', slotsInTimeZone); // Log the converted slots
      setAvailableSlots(slotsInTimeZone);
    } catch (error) {
      console.error('Error fetching available slots:', error);
    }
  };

  useEffect(() => {
    fetchUnavailableDates();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange = (date) => {
    setFormData({
      ...formData,
      appointmentDate: date,
    });
    fetchAvailableSlots(date); // Fetch available slots for the selected date
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check slot availability
      const availabilityResponse = await fetch(apiUrl(`/api/bookings/check-availability?appointmentDate=${formData.appointmentDate.toISOString()}`));

      if (!availabilityResponse.ok) {
        alert('Failed to check slot availability. Please try again.');
        return;
      }

      const isAvailable = await availabilityResponse.json();
      if (!isAvailable) {
        alert('The selected time slot is already booked. Please choose another time.');
        return;
      }

      // Proceed with booking if the slot is available
      const response = await fetch(apiUrl('/api/bookings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorMessage = await response.text();
        alert(errorMessage);
        return;
      }

      alert('Appointment successfully booked!');
      setFormData({
        name: '',
        email: '',
        phone: '',
        appointmentDate: new Date(),
        reason: '',
      });
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert('Failed to book appointment. Please try again.');
    }
  };

  return (
    <div className="font-sans">
      {/* Book Appointment Form Section */}
      <section className="py-16 px-4 bg-gray-100">
        <div className="max-w-2xl mx-auto bg-white p-8 rounded-xl shadow-md">
          <h2 className="text-3xl font-semibold mb-6 text-center">Book an Appointment</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-lg font-semibold text-gray-800">Full Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="email" className="block text-lg font-semibold text-gray-800">Email <span className="text-gray-500 text-sm">(Optional)</span></label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-lg font-semibold text-gray-800">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label htmlFor="appointmentDate" className="block text-lg font-semibold text-gray-800">Preferred Date and Time</label>
              <DatePicker
                selected={formData.appointmentDate}
                onChange={handleDateChange}
                showTimeSelect
                dateFormat="Pp"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                excludeDates={unavailableDates} // Disable unavailable dates
                includeTimes={availableSlots} // Show only available slots
                minDate={new Date()} // Disable past dates
                minTime={
                  formData.appointmentDate.toDateString() === new Date().toDateString()
                    ? new Date()
                    : new Date(0, 0, 0, 0, 0, 0)
                } // Disable past times for today
                maxTime={new Date(0, 0, 0, 23, 59, 59)} // Allow times up to 11:59 PM
                required
              />
            </div>
            <div>
              <label htmlFor="reason" className="block text-lg font-semibold text-gray-800">Reason for Visit (Optional)</label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleChange}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="4"
              />
            </div>
            <div className="text-center">
              <button
                type="submit"
                className="px-8 py-3 bg-blue-600 text-white text-xl font-semibold rounded-lg hover:bg-blue-700 focus:outline-none"
              >
                Submit
              </button>
            </div>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white text-center py-6">
        <p className="text-sm">&copy; {new Date().getFullYear()} Dr. Ashweeja Nagesh Clinic. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default BookAppointment;
